import { toast } from "@/hooks/use-toast";
import type { ErrorCode } from "@shared/errors";
import { isRoleRefusalEnvelope } from "@shared/writer-roles";
import {
  MutationCache,
  type Query,
  QueryCache,
  QueryClient,
  type QueryFunction,
  type QueryKey,
} from "@tanstack/react-query";
import {
  invalidateAccountingConnectionScopedQueries,
  isAccountingConnectionChangeRequest,
} from "./accounting-connection-change";
import type { PaginatedResponse } from "./use-paginated-query";

export function getCsrfToken(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.split(";").map((s) => s.trim()).find((s) => s.startsWith("csrf_token="));
  return match ? decodeURIComponent(match.slice("csrf_token=".length)) : undefined;
}

/**
 * OCT-3357 / OCT-3353: read the active-entity selection from localStorage
 * (the same key the `EntityProvider` writes). The server's active-entity
 * middleware validates this header, populates `req.context.entityId`,
 * and cross-checks against any URL/body `entityId` (E89). When no
 * entity is selected we omit the header; entity-scoped writes will
 * surface a typed error and the UI banner prompts the user to pick one.
 *
 * Internal state (`"all"`) vs wire value (`"*"`): the UI persists the
 * rollup pick as the human-readable `"all"` token, but the server's
 * active-entity middleware only recognises the `"*"` sentinel. We
 * translate at the wire boundary so the LS key keeps its current shape
 * (no migration) and a `"*"` value persisted by an older build still
 * works.
 *
 * @spec spec/enabling-system/entity-scoping.md §2 "The X-Octa-Entity-Id middleware"
 */
const ACTIVE_ENTITY_STORAGE_KEY = "octa-active-entity";
const ROLLUP_WIRE_SENTINEL = "*";
const ROLLUP_LOCAL_SENTINEL = "all";

export function getActiveEntityHeader(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(ACTIVE_ENTITY_STORAGE_KEY);
    if (raw == null || raw.trim() === "") return undefined;
    if (raw === ROLLUP_LOCAL_SENTINEL || raw === ROLLUP_WIRE_SENTINEL) return ROLLUP_WIRE_SENTINEL;
    return raw;
  } catch {
    return undefined;
  }
}

/**
 * OCT-3337 follow-up: routes that EntityProvider depends on to bootstrap
 * the active-entity context, plus workspace-scoped reads that have no
 * per-entity semantics. The active-entity header is never load-bearing
 * for these so we strip it client-side; this prevents a stale persisted
 * UUID from soft-bricking the app on first paint when the user lands in
 * a different workspace than the one they last used.
 *
 * Convention: any future route that needs entity scope must live OUTSIDE
 * `/api/workspace/*` (e.g. `/api/customers`, `/api/invoices`). The
 * `/api/workspace/` prefix is reserved for workspace-tier reads.
 *
 * @spec spec/enabling-system/entity-scoping.md §2
 */
export function isEntityHeaderSafeRoute(url: string): boolean {
  const path = stripQueryAndOrigin(url);
  if (path === "/api/entities") return true;
  if (path === "/api/workspace") return true;
  if (path.startsWith("/api/workspace/")) return true;
  // Pre-onboarding screens (ChoosePlanPage, OnboardingPage, UpgradeWelcomePage,
  // DowngradeNoticePage) render outside EntityProvider, so the entity-header
  // validation latch is never released for those flows. These two routes are
  // workspace-tier reads with no per-entity semantics and must bypass the
  // latch the same way `/api/workspace/*` does, otherwise the pre-onboarding
  // pages hang on first paint for new signups.
  if (path === "/api/plans") return true;
  if (path === "/api/billing/status") return true;
  // OCT-11353 follow-up: the public portals (`/portal/:customerId`,
  // `/vendor-portal/:vendorId`) are top-level routes in App.tsx, outside both
  // AuthProvider and EntityProvider. EntityProvider is the only caller of
  // `markEntityHeaderReady`, so on those pages the latch below is never
  // released and any `apiRequest` awaits it forever, with no request, no
  // timeout, and no error.
  //
  // Today exactly one portal callsite goes through `apiRequest`: the
  // customer portal's `pay-batch` mutation, which is why bank transfer / ACH
  // payment hung while raising a dispute and applying a credit did not - those
  // use raw `fetch` and never touch the latch. This bypass is the general rule
  // rather than a patch for that one callsite, so converting any other portal
  // fetch to `apiRequest` cannot reintroduce the hang.
  //
  // `/api/public/*` resolves its workspace and entity from the portal token it
  // carries, never from the active-entity header, so the header was never
  // load-bearing here. Stripping it also removes a live E89 hazard: an admin
  // opening a portal link in the same browser has the module-level latch
  // already open, so `apiRequest` would otherwise stamp a localStorage
  // `X-Octa-Entity-Id` onto a public request and risk a 400
  // CrossEntityContextError.
  if (path.startsWith("/api/public/")) return true;
  return false;
}

function stripQueryAndOrigin(url: string): string {
  let path = url;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    try {
      path = new URL(path).pathname;
    } catch {
      // fall through with the raw string
    }
  }
  const q = path.indexOf("?");
  return q === -1 ? path : path.slice(0, q);
}

/**
 * OCT-3337 follow-up: validation latch.
 *
 * Non-boot fetches `await waitForEntityValidation()` before firing, so
 * the request layer never stamps a stale (or yet-to-be-validated)
 * `X-Octa-Entity-Id` from localStorage. EntityProvider opens the latch
 * once it has reconciled the persisted LS value against the workspace's
 * entity list, OR if the entities query fails (so non-boot reads still
 * proceed with whatever the user has - the worst case is then the
 * pre-fix behaviour, not a hang).
 *
 * Boot routes (see `isEntityHeaderSafeRoute`) bypass the latch and the
 * header alike, which is what lets EntityProvider read the entity list
 * in the first place.
 */
let entityValidationResolve: (() => void) | null = null;
let entityValidationPromise: Promise<void> = new Promise<void>((resolve) => {
  entityValidationResolve = resolve;
});
let entityValidationReady = false;

export function waitForEntityValidation(): Promise<void> {
  return entityValidationPromise;
}

export function markEntityHeaderReady(): void {
  if (entityValidationReady) return;
  entityValidationReady = true;
  entityValidationResolve?.();
  entityValidationResolve = null;
}

/** Test-only: reset the latch between specs. Not exported from the index. */
export function __resetEntityValidationLatchForTest(): void {
  entityValidationReady = false;
  entityValidationPromise = new Promise<void>((resolve) => {
    entityValidationResolve = resolve;
  });
}

class AuthenticationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthenticationError";
  }
}

/**
 * Typed error thrown by `apiRequest` / `getQueryFn` for any non-2xx
 * response. Exposes the platform `code` (see `shared/errors.ts`) so
 * components can switch on stable codes instead of brittle messages.
 *
 * Task #2662: the legacy `errorType` mirror was removed. `code` is now the
 * sole platform-error discriminator. Other auxiliary fields (`errorCode`,
 * `validationErrors`, …) remain on the instance because route-specific
 * call sites still read them.
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code: ErrorCode;
  public readonly details?: unknown;
  public readonly correlationId?: string;
  /**
   * OCT-11301: seconds until the throttle that produced this error clears,
   * taken from the response's `Retry-After` (or `X-RateLimit-Reset`) header.
   *
   * Only ever set on a 429. It exists so the UI can tell the user *when* the
   * limit lifts instead of guessing - the limiter's window is 15 minutes and
   * the copy used to promise "about a minute", which is what drove users to
   * retry-spam a bucket that was already exhausted.
   */
  public readonly retryAfterSeconds?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public readonly body?: any;

  constructor(opts: {
    message: string;
    status: number;
    code: ErrorCode;
    details?: unknown;
    correlationId?: string;
    retryAfterSeconds?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body?: any;
  }) {
    super(opts.message);
    this.name = "ApiError";
    this.status = opts.status;
    this.code = opts.code;
    this.details = opts.details;
    if (opts.correlationId !== undefined) this.correlationId = opts.correlationId;
    if (opts.retryAfterSeconds !== undefined) this.retryAfterSeconds = opts.retryAfterSeconds;
    this.body = opts.body;
  }
}

/**
 * Read the throttle reset time off a 429 response.
 *
 * `Retry-After` is the authoritative header (the platform limiter always sets
 * it, see `server/lib/http-platform/middleware/rate-limit.ts`); the
 * `X-RateLimit-Reset` epoch-seconds header is the fallback for any proxy that
 * strips it. Returns `undefined` when neither is usable rather than inventing
 * a number - a wrong reset time is worse than none.
 */
export function parseRetryAfterSeconds(headers: Headers): number | undefined {
  const retryAfter = headers.get("retry-after");
  if (retryAfter) {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);
    const asDate = Date.parse(retryAfter);
    if (Number.isFinite(asDate)) {
      return Math.max(0, Math.ceil((asDate - Date.now()) / 1000));
    }
  }
  const reset = headers.get("x-ratelimit-reset");
  if (reset) {
    const epochSeconds = Number(reset);
    if (Number.isFinite(epochSeconds) && epochSeconds > 0) {
      return Math.max(0, Math.ceil(epochSeconds - Date.now() / 1000));
    }
  }
  return undefined;
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/** True for a throttled (HTTP 429) response. */
export function isRateLimitError(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 429;
}

/**
 * Query keys that make the app shell work at all (OCT-11301).
 *
 * These are read once at boot and are invariant across an entity switch, a
 * widget refresh or an entity-scope retry - refetching them from those places
 * is pure request volume against the very budget whose exhaustion strands the
 * shell. Cache invalidation that means "my scoped data may have changed"
 * must exclude them.
 */
const BOOTSTRAP_QUERY_KEYS: ReadonlySet<string> = new Set([
  "/api/auth/me",
  "/api/workspace",
  "/api/workspace/features",
  "/api/workspace/profile-config",
  "/api/entities",
  "/api/billing/status",
  "/api/plans",
]);

/** True when `key` is the first segment of an app-bootstrap query key. */
export function isBootstrapQueryKey(key: unknown): boolean {
  return typeof key === "string" && BOOTSTRAP_QUERY_KEYS.has(key);
}

export function isHtmlBody(text: string): boolean {
  if (!text) return false;
  const head = text.trimStart().slice(0, 200).toLowerCase();
  return head.startsWith("<!doctype") || head.startsWith("<html") || head.startsWith("<!--");
}

export function isJsonContentType(contentType: string | null | undefined): boolean {
  if (!contentType) return false;
  const ct = contentType.toLowerCase();
  return ct.includes("application/json") || ct.includes("+json");
}

/** OCT-17302 / ILLEGAL E157: the one message a Viewer sees when a write is refused. */
export const VIEWER_READ_ONLY_MESSAGE = "Your role can view but not change this.";

/**
 * OCT-22449 / ILLEGAL E165: the one message any other role sees when a role
 * guard (`withAdmin`, `withRole`, `withWriter`) refuses the request. Never
 * "Not found", "Network error" or a generic failure. Translated as
 * `permissions.roleRefusal` in every locale.
 */
export const ROLE_REFUSAL_MESSAGE = "Your role can't do this. Ask a workspace admin.";

export function extractErrorMessage(text: string, status: number, contentType?: string | null): string {
  if (
    isJsonContentType(contentType) || (text && (text.trimStart().startsWith("{") || text.trimStart().startsWith("[")))
  ) {
    try {
      const json = JSON.parse(text) as { code?: string; details?: unknown; userMessage?: string; message?: string; };
      if (json.code === "VIEWER_READ_ONLY") return VIEWER_READ_ONLY_MESSAGE;
      if (isRoleRefusalEnvelope(json.code, json.details)) return ROLE_REFUSAL_MESSAGE;
      if (json.userMessage) return json.userMessage;
      if (json.message) return json.message;
    } catch {} /* JSON.parse fallback — non-JSON response handled below */
  }

  if (isHtmlBody(text)) {
    if (status >= 500) return "Server error. Please try again.";
    return "Couldn't load data. Please try again.";
  }

  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You don't have permission to perform this action.";
  if (status === 429) return "Too many requests. Please try again later.";
  if (text && text.length > 0) return text;
  if (status >= 500) return "An unexpected error occurred. Please try again.";
  return "Request failed";
}

export async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const contentType = res.headers.get("content-type");
    const text = (await res.text()) || res.statusText;
    const message = extractErrorMessage(text, res.status, contentType);
    if (res.status === 401) {
      throw new AuthenticationError(message);
    }

    let json: Record<string, unknown> | null = null;
    if (isJsonContentType(contentType) || text.trimStart().startsWith("{") || text.trimStart().startsWith("[")) {
      try {
        const parsed: unknown = JSON.parse(text);
        if (parsed && typeof parsed === "object") {
          json = parsed as Record<string, unknown>;
        }
      } catch {} /* non-JSON body — ignore */
    }

    // Prefer the new typed envelope { code, message, details, correlationId }
    // and fall back to inferring `code` from the HTTP status for legacy
    // ad-hoc routes that still respond with `{ message }` only.
    const code: ErrorCode = (json && json.code) ? (json.code as ErrorCode) : legacyCodeFromStatus(res.status);
    const correlationId = json?.correlationId as string | undefined;
    const retryAfterSeconds = res.status === 429 ? parseRetryAfterSeconds(res.headers) : undefined;
    const error = new ApiError({
      message,
      status: res.status,
      code,
      details: json?.details,
      ...(correlationId !== undefined && { correlationId }),
      ...(retryAfterSeconds !== undefined && { retryAfterSeconds }),
      body: json,
    });
    // Back-compat: preserve commonly-read fields on the error instance.
    if (json) {
      const target = error as unknown as Record<string, unknown>;
      if (json.errorCode) target.errorCode = json.errorCode;
      if (json.missingTrigger !== undefined) target.missingTrigger = json.missingTrigger;
      if (json.missingAction !== undefined) target.missingAction = json.missingAction;
      if (json.stale) target.stale = json.stale;
      if (json.updatedInvoices) target.updatedInvoices = json.updatedInvoices;
      if (json.validationErrors) target.validationErrors = json.validationErrors;
      if (json.validationWarnings) target.validationWarnings = json.validationWarnings;
      if (json.warnings) target.validationWarnings = json.warnings;
      if (json.errors) target.errors = json.errors;
      if (json.feature) target.feature = json.feature;
    }
    throw error;
  }
}

function legacyCodeFromStatus(status: number): ErrorCode {
  if (status === 400) return "VALIDATION_FAILED";
  if (status === 401) return "UNAUTHORIZED";
  if (status === 402) return "PAYMENT_DECLINED";
  if (status === 403) return "FORBIDDEN";
  if (status === 404) return "NOT_FOUND";
  if (status === 409) return "CONFLICT";
  if (status === 422) return "EINVOICE_REJECTED";
  if (status === 429) return "RATE_LIMITED";
  if (status >= 500) return "INTERNAL_ERROR";
  return "VALIDATION_FAILED";
}

/**
 * Parse a successful response as JSON, but throw a friendly error if the body
 * isn't actually JSON (e.g., the SPA catch-all returned HTML during a server
 * restart). Prevents raw `Unexpected token '<'` errors from leaking to users.
 */
export async function safeJson<T = unknown>(res: Response): Promise<T> {
  const contentType = res.headers.get("content-type");
  if (isJsonContentType(contentType)) {
    return (await res.json()) as T;
  }
  const text = await res.text();
  if (!text) {
    return undefined as unknown as T;
  }
  if (isHtmlBody(text)) {
    const err = new Error("Couldn't load data. Please try again.") as Error & {
      status?: number;
      nonJsonBody?: boolean;
    };
    err.status = res.status;
    err.nonJsonBody = true;
    throw err;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    const err = new Error("Couldn't load data. Please try again.") as Error & {
      status?: number;
      nonJsonBody?: boolean;
    };
    err.status = res.status;
    err.nonJsonBody = true;
    throw err;
  }
}

export { AuthenticationError };

export function getHttpErrorStatus(error: unknown): number | undefined {
  if (error instanceof Error && "status" in error && typeof (error as Record<string, unknown>).status === "number") {
    return (error as Record<string, unknown>).status as number;
  }
  return undefined;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (data) headers["Content-Type"] = "application/json";
  const csrf = getCsrfToken();
  if (csrf) headers["x-csrf-token"] = csrf;
  // OCT-3337 follow-up: boot/workspace-tier routes never need the
  // active-entity header. Skipping them on the wire AND skipping the
  // validation latch is what lets EntityProvider read the entity list
  // when a stale LS value would otherwise resolve to a different
  // workspace's UUID and 400 the request.
  const safe = isEntityHeaderSafeRoute(url);
  if (!safe) {
    await waitForEntityValidation();
    // OCT-3357 / OCT-3353: thread the active entity through every
    // non-boot API call so the server's active-entity middleware can
    // populate the request context and cross-check against any body /
    // URL `entityId`.
    const activeEntity = getActiveEntityHeader();
    if (activeEntity) headers["X-Octa-Entity-Id"] = activeEntity;
  }

  const res = await fetch(url, {
    method,
    headers,
    credentials: "include",
    ...(data !== undefined && data !== null ? { body: JSON.stringify(data) } : {}),
  });

  await throwIfResNotOk(res);
  // OCT-18620: one place refreshes the GL pickers after any connection
  // change, so a new connect / disconnect / sync path cannot miss it.
  if (isAccountingConnectionChangeRequest(method, url)) {
    void invalidateAccountingConnectionScopedQueries(queryClient);
  }
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export function getQueryFn<T>({ on401: unauthorizedBehavior }: {
  on401: UnauthorizedBehavior;
}): QueryFunction<T> {
  return (async ({ queryKey }: { queryKey: QueryKey; }) => {
    const url = (queryKey as readonly unknown[]).join("/");
    const headers: Record<string, string> = {};
    // OCT-3337 follow-up: boot/workspace-tier routes never need the
    // active-entity header (see `isEntityHeaderSafeRoute`). Skipping
    // both the latch and the header for them is what lets the entity
    // list bootstrap even when the persisted LS value is stale.
    if (!isEntityHeaderSafeRoute(url)) {
      await waitForEntityValidation();
      // OCT-3357 / OCT-3353: include the active-entity header on reads
      // so the server can resolve the active-entity context for per-
      // entity list filters and the "All Entities" rollup sentinel.
      const activeEntity = getActiveEntityHeader();
      if (activeEntity) headers["X-Octa-Entity-Id"] = activeEntity;
    }

    const res = await fetch(url, {
      headers,
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await safeJson(res);
  }) as QueryFunction<T>;
}

/**
 * Throttle recovery (OCT-11301).
 *
 * A 429 is the one 4xx that is transient *by definition* - it carries the time
 * at which it stops being true. Before this, a throttled read was treated like
 * any other deterministic 4xx: never retried, and then pinned forever by
 * `staleTime: Infinity`. A single unlucky bootstrap read therefore stayed
 * failed for the rest of the session, which is why a hard refresh did not
 * recover the app.
 *
 * Recovery is a *scheduled, per-key* invalidation rather than TanStack's
 * inline `retry`, for two reasons:
 *
 *  - An inline retry keeps the query in `status: "pending"` for the whole
 *    backoff. With a 15-minute `Retry-After` that would hide the honest
 *    "you are being throttled" UI behind a loading skeleton for 15 minutes -
 *    the opposite of the recoverable state this ticket asks for. Failing fast
 *    and re-arming later gives the user a real error card *and* a self-heal.
 *  - It cannot burst. There is at most ONE armed timer per query key, it fires
 *    no earlier than the server's own `Retry-After`, it is jittered so five
 *    bootstrap reads that were throttled together do not resume in lockstep,
 *    and it re-arms at most `MAX_RATE_LIMIT_RECOVERIES` times before a
 *    success resets the count. Blanket-retrying 429s would have amplified the
 *    very storm this ticket is about.
 */
const RATE_LIMIT_RECOVERY_MIN_MS = 5_000;
const RATE_LIMIT_RECOVERY_MAX_MS = 15 * 60 * 1000;
const RATE_LIMIT_RECOVERY_FALLBACK_MS = 60_000;
const RATE_LIMIT_RECOVERY_JITTER_MS = 2_000;
const MAX_RATE_LIMIT_RECOVERIES = 2;

const rateLimitRecoveryTimers = new Map<string, ReturnType<typeof setTimeout>>();
const rateLimitRecoveryCounts = new Map<string, number>();

/**
 * How long to wait before re-arming a throttled query, in ms.
 *
 * Honours the server's `Retry-After` when it sent one, clamped into a sane
 * band: never sooner than 5s (a stampede guard for a proxy that returns a
 * nonsense `0`), never later than the limiter's own 15-minute window.
 * Exported for unit testing.
 */
export function rateLimitRecoveryDelayMs(retryAfterSeconds: number | undefined): number {
  const base = retryAfterSeconds === undefined
    ? RATE_LIMIT_RECOVERY_FALLBACK_MS
    : retryAfterSeconds * 1000;
  const clamped = Math.min(
    RATE_LIMIT_RECOVERY_MAX_MS,
    Math.max(RATE_LIMIT_RECOVERY_MIN_MS, base),
  );
  return clamped + Math.floor(Math.random() * RATE_LIMIT_RECOVERY_JITTER_MS);
}

/** The `QueryCache` callback shape - a query of any key/data/error type. */
type AnyQuery = Query<unknown, unknown, unknown, readonly unknown[]>;

function scheduleRateLimitRecovery(error: ApiError, query: AnyQuery): void {
  const hash = query.queryHash;
  // One armed timer per key. A burst of throttled reads on the same key
  // (several observers, several remounts) must arm exactly one refetch.
  if (rateLimitRecoveryTimers.has(hash)) return;
  const attempts = rateLimitRecoveryCounts.get(hash) ?? 0;
  if (attempts >= MAX_RATE_LIMIT_RECOVERIES) return;
  rateLimitRecoveryCounts.set(hash, attempts + 1);

  const timer = setTimeout(() => {
    rateLimitRecoveryTimers.delete(hash);
    void queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true });
  }, rateLimitRecoveryDelayMs(error.retryAfterSeconds));
  rateLimitRecoveryTimers.set(hash, timer);
}

/** Disarm the pending recovery refetch for one query, if it has one. */
function cancelRateLimitRecovery(hash: string): void {
  const timer = rateLimitRecoveryTimers.get(hash);
  if (timer !== undefined) {
    clearTimeout(timer);
    rateLimitRecoveryTimers.delete(hash);
  }
}

/**
 * Disarm every pending recovery refetch and forget the per-key attempt counts
 * (OCT-11301).
 *
 * Must be called wherever the cache itself is torn down - session expiry and
 * logout both `queryClient.clear()`. A timer armed just before teardown would
 * otherwise still fire minutes later and invalidate against an emptied cache.
 * That happens to be a no-op today only because TanStack finds no matching
 * query; relying on that is an implicit contract, and the attempt counts would
 * still carry into the next signed-in session and silently shrink its recovery
 * budget. Teardown is explicit instead.
 */
export function cancelPendingRateLimitRecoveries(): void {
  for (const timer of rateLimitRecoveryTimers.values()) clearTimeout(timer);
  rateLimitRecoveryTimers.clear();
  rateLimitRecoveryCounts.clear();
}

/** Test-only view of the recovery bookkeeping (OCT-11301). */
export function __rateLimitRecoveryStateForTest(): { timers: number; counts: number; } {
  return { timers: rateLimitRecoveryTimers.size, counts: rateLimitRecoveryCounts.size };
}

const queryCache = new QueryCache({
  onError: (error: Error, query: AnyQuery) => {
    if (error instanceof AuthenticationError) {
      handleAuthExpiry();
      return;
    }
    if (isRateLimitError(error)) {
      scheduleRateLimitRecovery(error, query);
    }
  },
  onSuccess: (_data: unknown, query: AnyQuery) => {
    // The throttle cleared - give this key its full recovery budget back and
    // disarm the refetch we had queued for it, so a query that recovers on its
    // own (window focus, a user action, a sibling observer) does not also get a
    // redundant invalidation when its old timer comes due.
    cancelRateLimitRecovery(query.queryHash);
    rateLimitRecoveryCounts.delete(query.queryHash);
  },
});

const mutationCache = new MutationCache({
  onError: (error: Error, _variables, _context, mutation) => {
    if (error instanceof AuthenticationError) {
      handleAuthExpiry();
      return;
    }
    // OCT-17302 / ILLEGAL E157, OCT-22449 / ILLEGAL E165: a refused write is
    // never silent. A mutation with its own onError shows its own message (the
    // same text, via extractErrorMessage), so only the rest get the shared
    // toast. `error.message` is already the Viewer or role-refusal copy.
    if (error instanceof ApiError && isRoleRefusalEnvelope(error.code, error.details) && !mutation.options.onError) {
      toast({ title: error.message, variant: "destructive" });
    }
  },
});

/**
 * A failure worth retrying is a *transient* one — a momentary blip that will
 * likely succeed on a second attempt (server restart during a deploy, a wifi
 * switch, a token-renewal race, a 5xx). Deterministic failures must NOT retry:
 * a 401 is routed to the session-expiry handler, and 4xx (validation, forbidden,
 * not-found, conflict) will fail identically on retry, so retrying only delays
 * the error the UI needs to show.
 *
 * Why this matters for app responsiveness: with `staleTime: Infinity`,
 * `refetchOnWindowFocus: false` and the previous `retry: false`, a single
 * transient background failure left a query stuck in its error state with no
 * automatic recovery path — the only way back was a manual page refresh, the
 * exact symptom users reported. Bounded transient retry + `refetchOnReconnect`
 * lets the UI self-heal instead.
 *
 * 429 is deliberately still excluded here (OCT-11301). It IS transient, but an
 * inline retry would hold the query in `status: "pending"` for the whole
 * `Retry-After` - up to 15 minutes of loading skeleton instead of the honest
 * "you are being throttled" card. It recovers through
 * `scheduleRateLimitRecovery` above instead: fail fast, re-arm once at the
 * server's own reset time.
 */
function isTransientQueryError(error: unknown): boolean {
  if (error instanceof AuthenticationError) return false;
  if (error instanceof ApiError) return error.status >= 500;
  // Network errors (fetch rejects with a TypeError) and the generic
  // Error("Failed"/"Failed to fetch") thrown by ad-hoc fetchers after their own
  // explicit 401 handling are treated as transient.
  return true;
}

const MAX_TRANSIENT_RETRIES = 2;

export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      // Recover automatically when connectivity returns rather than stranding
      // the user on stale/empty data until they refresh.
      refetchOnReconnect: true,
      staleTime: Infinity,
      // Keep cached data for inactive modules around long enough that revisiting
      // a recently-viewed module is instant rather than re-fetching from scratch.
      // (TanStack default is 5m, which can evict a module's data between visits.)
      gcTime: 1000 * 60 * 30,
      retry: (failureCount, error) => failureCount < MAX_TRANSIENT_RETRIES && isTransientQueryError(error),
    },
    mutations: {
      // Mutations are never auto-retried — a retried write risks a duplicate
      // side effect (double charge, double-create). Callers handle their own
      // recovery explicitly.
      retry: false,
    },
  },
});

/**
 * Session-expiry handler shared by the query and mutation caches. Any authed
 * request that returns 401 surfaces as an `AuthenticationError`; when that
 * happens the in-memory session is *probably* stale, so we want to clear the
 * cached user + all query data and bounce to the landing page to force
 * re-authentication.
 *
 * Before forcing a logout we revalidate against the auth source-of-truth
 * (`/api/auth/me`): only a confirmed 401 there means the session is genuinely
 * dead. This prevents a single endpoint emitting a spurious/endpoint-scoped 401
 * from evicting a still-valid session (false-positive logout).
 *
 * The `handlingAuthExpiry` guard collapses the burst of concurrent 401s a
 * single screen can emit (e.g. ChoosePlanPage firing `/api/plans` +
 * `/api/billing/status`, or the dashboard's many widgets) into ONE revalidation.
 *
 * Recovery is driven purely through auth *state*, never a hard navigation:
 * `AppContent` renders `<AuthPage/>` whenever the cached `/api/auth/me` user is
 * null, so flipping that cache entry to null swaps the whole app to the sign-in
 * screen in place. Earlier revisions hard-reloaded to `/?auth_error=…`; on the
 * landing route that re-mounted the providers, re-fired the authed queries,
 * 401'd again, and reloaded — an infinite loop. We now `clear()` the cache (so
 * unmounted widgets cannot refetch and re-enter this handler) and latch the
 * guard until the next full page load (a genuine login resets module state).
 */
let handlingAuthExpiry = false;
function handleAuthExpiry() {
  if (handlingAuthExpiry) return;
  handlingAuthExpiry = true;
  void revalidateSession();
}

async function revalidateSession() {
  try {
    const res = await fetch("/api/auth/me", { credentials: "include" });
    if (res.status !== 401) {
      // Endpoint-scoped 401 (e.g. a permission denial), not session expiry —
      // the session is still valid, so leave the cached user intact and allow
      // a future genuine expiry to be handled.
      handlingAuthExpiry = false;
      return;
    }
  } catch {
    handlingAuthExpiry = false;
    return;
  }
  // Session is genuinely dead. Drop all cached authed data so unmounted widgets
  // cannot refetch, then flip the auth gate to null — AppContent re-renders to
  // <AuthPage/> in place. No hard reload (the historical loop source); the
  // guard stays latched until the next full page load.
  queryClient.clear();
  // The cache is gone; nothing may still be scheduled against it (OCT-11301).
  cancelPendingRateLimitRecoveries();
  queryClient.setQueryData(["/api/auth/me"], null);
  // Surface the expiry message on the sign-in screen without reloading;
  // AuthPage reads `auth_error` from the URL on mount.
  try {
    if (!new URLSearchParams(window.location.search).has("auth_error")) {
      window.history.replaceState(
        {},
        "",
        "/?auth_error="
          + encodeURIComponent("Your session has expired. Please sign in again."),
      );
    }
  } catch {} /* history API unavailable — message is best-effort */
}

export type QuerySnapshot<T = unknown> = [QueryKey, T | undefined][];

export async function optimisticListUpdate<T extends { id: string; }>(
  baseQueryKey: string,
  ids: string[],
  updater: (item: T) => T,
): Promise<QuerySnapshot<PaginatedResponse<T>>> {
  await queryClient.cancelQueries({ queryKey: [baseQueryKey] });
  const previousQueries = queryClient.getQueriesData<PaginatedResponse<T>>({ queryKey: [baseQueryKey] });
  const idSet = new Set(ids);
  queryClient.setQueriesData<PaginatedResponse<T>>(
    { queryKey: [baseQueryKey] },
    (old) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.map((item) => (idSet.has(item.id) ? updater(item) : item)),
      };
    },
  );
  return previousQueries;
}

export function rollbackFromSnapshot<T = unknown>(snapshot: QuerySnapshot<T> | undefined): void {
  if (!snapshot) return;
  for (const [key, data] of snapshot) {
    queryClient.setQueryData(key, data);
  }
}
